var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc scatter
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group } from '../../element';
import { LegendType } from '../../interface/legend';
import { SeriesModel } from '../../model';
import { getRectLabelPosition } from '../../utils/textLayout';
import { registerChart } from '../index';
import { createSymbol, getSymbolAttr, getSymbolShapeFromCenter } from '../../symbol';
import { createRichText } from '../../rich';
import { getSymbolSize } from '../../utils/styleHelper';
/**
 * default
 */
var ScatterModel = /** @class */ (function (_super) {
    __extends(ScatterModel, _super);
    function ScatterModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'scatter';
        _this.legendType = LegendType.SERIES_LEGEND;
        _this.legendIcon = 'rect';
        _this.handleMapRoam = function (event) {
            if (_this.option.coord !== 'geo') {
                return;
            }
            var scale = event.scale, position = event.position;
            _this.setState({ scale: scale, position: position });
            _this.state.points.forEach(function (point) {
                var _a;
                var x = point.x, y = point.y;
                point.plotX = x * scale + position[0];
                point.plotY = y * scale + position[1];
                var shapePrama = getSymbolShapeFromCenter(point.plotX, point.plotY, point.visible && _this.visible() ? point.size : 0);
                var attr = getSymbolAttr(_this.getPointSymbolShapeStyle(point), shapePrama);
                (_a = point.shapeRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(attr);
            });
        };
        return _this;
    }
    ScatterModel.prototype.getDefaultOption = function () {
        return __assign(__assign({}, _super.prototype.getDefaultOption.call(this)), { type: 'scatter', name: '', maxSize: '10%', xAxis: 0, yAxis: 0, coord: 'cartesian2d', polar: 0, shapeStyle: {
                symbol: null,
                lineWidth: 1,
                stroke: null,
                fillOpacity: 0.3,
            }, encode: {
                x: '',
                y: '',
                name: '',
                size: 4,
                shape: 'circle',
                color: null,
            }, label: {
                show: false,
                position: 'outside',
                distance: 4,
                autoHide: false,
                offsetX: 0,
                offsetY: 0,
                fontSize: 12,
                rotate: null,
                color: '#333333',
                formatter: function (point) { return point.name; },
            }, hover: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
                label: {
                    color: null,
                    fontSize: null,
                },
            }, selected: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            }, animate: null, visible: true, showLegend: true, selectedMode: false });
    };
    ScatterModel.prototype.getInitialState = function () {
        return {
            points: [],
            needAnimation: this.needAnimation(),
            scale: 1,
            position: [0, 0],
        };
    };
    ScatterModel.prototype.init = function () {
        this.registerEvent('map:roam', this.handleMapRoam);
    };
    ScatterModel.prototype.getScatterPoints = function () {
        var _this = this;
        var _a = this.state, scale = _a.scale, position = _a.position;
        var coordModel = this.chart.getComponentModel('coord');
        var coord = coordModel.getCoordinate(this.option);
        if (!coord) {
            return [];
        }
        var dataset = this.getDataset();
        var sizeEncode = this.option.encode.size;
        return dataset.mapData(function (record, dataIndex) {
            var encodeData = _this._encode.getEncodeData(record, dataIndex);
            var _a = coord.dataToPoint(encodeData, true), x = _a.x, y = _a.y, outside = _a.outside;
            var sizeField = lodash.isArray(sizeEncode) ? sizeEncode[0] : '';
            return {
                encode: encodeData,
                x: x,
                y: y,
                plotX: x * scale + position[0],
                plotY: y * scale + position[1],
                name: encodeData.name || '',
                value: sizeField ? record[sizeField] : encodeData.y,
                size: encodeData.size,
                color: encodeData.color,
                visible: !outside && !_this.invisibleItems.has(dataIndex),
                shapeRef: createRef(),
                labelRef: createRef(),
                selected: _this.selectedItems.has(record),
            };
        });
    };
    ScatterModel.prototype.beforeRender = function () {
        if (this.visible()) {
            this.state.points = this.getScatterPoints();
        }
    };
    ScatterModel.prototype.afterRender = function () { };
    ScatterModel.prototype.update = function (nextOption, nextIndex, mergedOld) {
        _super.prototype.update.call(this, nextOption, nextIndex, mergedOld);
        this.setState({ scale: 1, position: [0, 0] });
    };
    ScatterModel.prototype.getPointSymbolShapeStyle = function (point) {
        return __assign(__assign({}, this.option.shapeStyle), { symbol: this.option.shapeStyle.symbol || point.encode.shape });
    };
    ScatterModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        if (this.isLargeData()) {
            return;
        }
        var point = this.state.points[dataIndex];
        var selected = point.selected, color = point.color, shapeRef = point.shapeRef, labelRef = point.labelRef;
        var pointEl = shapeRef.current;
        if (!pointEl) {
            return;
        }
        pointEl.setAttr(__assign(__assign({ fill: active ? this.getHoverColor(color) : color }, this.getShapeStyle(active ? this.option.hover.shapeStyle : this.getPointSymbolShapeStyle(point))), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
        if (labelRef && labelRef.current) {
            labelRef.current.setAttr(__assign({}, this.getTextStyle(active ? this.option.hover.label : this.option.label)));
        }
    };
    ScatterModel.prototype.toggleSelectPoint = function (dataIndex, selected) {
        var _a;
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var point = this.state.points[dataIndex];
        (_a = point.shapeRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(__assign(__assign({ fill: point.color }, this.getShapeStyle(this.option.shapeStyle)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
    };
    ScatterModel.prototype.renderScatterPoints = function () {
        var _this = this;
        var _a = this.option.shapeStyle, fill = _a.fill, stroke = _a.stroke;
        var fillColor = fill || this.getSeriesColor();
        var strokeColor = stroke || this.getSeriesColor();
        var colorEncode = this.option.encode.color;
        var group = new Group(__assign({ key: this.getKey('scatter-points'), fill: fillColor, stroke: strokeColor }, this.getShapeStyle(this.option.shapeStyle)));
        var shapeList = [];
        var needAnimation = this.needAnimation();
        var transitionProperty = this.getTransitionProperty();
        this.state.points.forEach(function (point, dataIndex) {
            var plotX = point.plotX, plotY = point.plotY, _a = point.size, size = _a === void 0 ? 0 : _a, visible = point.visible, selected = point.selected;
            if (!visible) {
                return false;
            }
            var pointSize = point.visible && _this.visible() ? size : 0;
            var symbolParam = getSymbolShapeFromCenter(plotX, plotY, pointSize);
            var shape = createSymbol(_this.getPointSymbolShapeStyle(point), symbolParam, __assign(__assign(__assign({ key: dataIndex, ref: point.shapeRef }, (colorEncode
                ? _this.getShapeStyle({
                    fill: fill || point.color,
                    stroke: stroke || point.color,
                })
                : null)), (selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)), { cursor: 'pointer', transitionProperty: transitionProperty, onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: _this.option.name,
                        point: _this.getWrappedPoint(dataIndex),
                    });
                }, onMounted: needAnimation
                    ? function () {
                        var animateFromSymbolParam = getSymbolShapeFromCenter(plotX, plotY, 1e-6);
                        var animateToSymbolParam = getSymbolShapeFromCenter(plotX, plotY, size);
                        var animateFromAttr = getSymbolAttr(_this.getPointSymbolShapeStyle(point), animateFromSymbolParam);
                        var animateToAttr = getSymbolAttr(_this.getPointSymbolShapeStyle(point), animateToSymbolParam);
                        shape.setAttr(animateFromAttr).animateTo(animateToAttr, 1000);
                    }
                    : null }));
            shapeList.push(shape);
        });
        if (shapeList.length > this.option.chunkLimit) {
            group.replaceChunks(this.getChunks(shapeList));
        }
        else {
            group.addAll(shapeList);
        }
        return group;
    };
    ScatterModel.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({ key: 'scatter-label' });
        var _a = this.option.label, show = _a.show, position = _a.position, distance = _a.distance, offsetX = _a.offsetX, offsetY = _a.offsetY, autoHide = _a.autoHide;
        var inside = position.indexOf('inside') !== -1;
        var labels = this.state.points.map(function (point, dataIndex) {
            var plotX = point.plotX, plotY = point.plotY, _a = point.size, size = _a === void 0 ? 0 : _a, visible = point.visible, color = point.color, labelRef = point.labelRef;
            var cx = plotX + offsetX;
            var cy = plotY + offsetY;
            var _b = getSymbolSize(size), width = _b[0], height = _b[1];
            var textPosition = getRectLabelPosition({
                top: cy - height / 2,
                left: cx - width / 2,
                right: cx + width / 2,
                bottom: cy + height / 2,
            }, position, distance);
            return createRichText(__assign(__assign(__assign({ ref: labelRef, display: show && _this.visible() && visible, text: _this.getDataLabel(dataIndex), fill: inside ? '#ffffff' : color, pointerEvents: inside ? 'none' : 'auto' }, textPosition), _this.getTextStyle(_this.option.label)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'label', {
                        point: _this.getWrappedPoint(dataIndex),
                    });
                } }));
        });
        autoHide && this.chart.__autoHideLabel(labels);
        return group.addAll(labels);
    };
    ScatterModel.prototype.render = function () {
        var group = new Group({
            key: this.id,
        });
        group.add(this.renderScatterPoints());
        if (this.option.label.show) {
            group.add(this.renderLabels());
        }
        return group;
    };
    ScatterModel.dependencies = ['coord', 'legend'];
    return ScatterModel;
}(SeriesModel));
export default ScatterModel;
registerChart('scatter', ScatterModel);
